import { FACE, LEVEL_LINE, SHADOW_RGB, SIDE_EDGE, WATER } from "@/lib/terrain";

// 3D BOARD: the two side faces of one square's block, drawn in the square's own footprint
// (0..S x 0..S, the top face sits at (-d, -d)). Right face (S-d,-d) (S,0) (S,S) (S-d,S-d);
// bottom face (-d,S-d) (S-d,S-d) (S,S) (0,S). One level line per level, `step` px apart:
// vertical on the right face, horizontal on the bottom face. Never takes clicks.
export function SideFaces({ S, height, step, flooded = false, testid }) {
  const lv = Math.max(1, Number(height) || 1);
  const d = step * lv;
  const col = FACE[lv] || FACE[1];
  const right = flooded ? WATER.right : col.right;
  const bottom = flooded ? WATER.bottom : col.bottom;
  const line = flooded ? WATER.lines : LEVEL_LINE;
  const lw = 1.4 * Math.max(0.75, step / 5);
  const ts = Array.from({ length: lv }, (_, j) => (j + 1) * step - lw / 2);
  return (
    <svg
      data-testid={testid}
      data-height={lv}
      data-flooded={flooded ? "true" : undefined}
      className="absolute pointer-events-none"
      style={{ left: 0, top: 0, overflow: "visible" }}
      width={S}
      height={S}
      aria-hidden="true"
    >
      <polygon points={`${S - d},${-d} ${S},0 ${S},${S} ${S - d},${S - d}`} fill={right} />
      <polygon points={`${-d},${S - d} ${S - d},${S - d} ${S},${S} 0,${S}`} fill={bottom} />
      {ts.map((t) => (
        <g key={t}>
          <line x1={S - d + t} y1={-d + t} x2={S - d + t} y2={S - d + t} stroke={line} strokeWidth={lw} />
          <line x1={-d + t} y1={S - d + t} x2={S - d + t} y2={S - d + t} stroke={line} strokeWidth={lw} />
        </g>
      ))}
      <polyline points={`${S - d},${-d} ${S},0 ${S},${S} 0,${S} ${-d},${S - d}`} fill="none" stroke={SIDE_EDGE} strokeWidth={1} strokeLinejoin="round" />
      <line x1={S - d} y1={S - d} x2={S} y2={S} stroke={SIDE_EDGE} strokeWidth={1} />
    </svg>
  );
}

// Shadows a higher neighbour casts on this top face (light from the top-left): a strip down the
// left edge (higher left neighbour) / along the top edge (higher neighbour above), 9 px per level
// of difference, and a radial corner shadow (higher diagonal up-left neighbour), 12 px per level.
export function FaceShadows({ shade, k, cellKey }) {
  const parts = [];
  if (shade.left > 0) {
    const a = 0.14 + 0.08 * shade.left;
    parts.push(`linear-gradient(90deg, rgba(${SHADOW_RGB},${a}) 0, rgba(${SHADOW_RGB},0) ${9 * shade.left * k}px)`);
  }
  if (shade.top > 0) {
    const a = 0.14 + 0.08 * shade.top;
    parts.push(`linear-gradient(180deg, rgba(${SHADOW_RGB},${a}) 0, rgba(${SHADOW_RGB},0) ${9 * shade.top * k}px)`);
  }
  if (shade.corner > 0) {
    const a = 0.12 + 0.06 * shade.corner;
    const r = 12 * shade.corner * k;
    parts.push(`radial-gradient(circle ${r}px at 0 0, rgba(${SHADOW_RGB},${a}) 0, rgba(${SHADOW_RGB},0) ${r}px)`);
  }
  if (!parts.length) return null;
  return (
    <span
      data-testid={`face-shadow-${cellKey}`}
      data-shadow={`${shade.left},${shade.top},${shade.corner}`}
      className="absolute inset-0 pointer-events-none"
      style={{ background: parts.join(", "), zIndex: 8 }}
    />
  );
}

// Three thin pale wave lines on a flooded top face.
export function FloodWaves() {
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
      {[30, 55, 80].map((y, i) => (
        <path
          key={y}
          d={`M${8 + i * 4} ${y} Q ${18 + i * 4} ${y - 5}, ${28 + i * 4} ${y} T ${48 + i * 4} ${y} T ${68 + i * 4} ${y} T ${88 + i * 4} ${y}`}
          fill="none"
          stroke="rgba(234,243,246,0.75)"
          strokeWidth="1.2"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
}
